import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  CheckboxField,
  EmptyState,
  NumberField,
  Pill,
  SectionHeader,
  StatCard,
} from "../../components/design-system/index.js";
import {
  getRebalance,
  type RebalanceResponse,
} from "../../lib/api-modules/portfolio.js";
import { formatCentsCurrency } from "../../lib/util/money.js";
import { SuggestedPromptsTile } from "../../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../../lib/data/page-prompts.js";

/**
 * /portfolio/rebalance — Phase 11 Cleave 3.
 *
 * Cash-inflow rebalance optimizer. v1 default: no sells in any account
 * (mirrors the helper's behavior). Toggle "Allow sells" to surface
 * sell recommendations.
 */
export function PortfolioRebalancePage() {
  const [cashDollars, setCashDollars] = useState<string>("0");
  const [allowSells, setAllowSells] = useState(false);
  const cashCents = Math.max(0, Math.round(Number(cashDollars || 0) * 100));
  const { data, isLoading, error, refetch, isFetching } =
    useQuery<RebalanceResponse>({
      queryKey: ["portfolio", "rebalance", cashCents, allowSells],
      queryFn: () =>
        getRebalance({ cash_inflow_cents: cashCents, allow_sells: allowSells }),
    });

  return (
    <div className="mx-auto max-w-4xl space-y-6 px-4 py-8 sm:px-6">
      <SectionHeader
        title="Rebalance"
        sub="Suggests trades to drift the portfolio toward your target allocation. v1 prefers cash-inflow over sells."
        action={
          <Link
            to="/portfolio"
            className="rounded-md border border-[var(--color-border-default)] px-3 py-1.5 text-xs font-medium text-[var(--color-muted)] hover:bg-[var(--color-elevated)] hover:text-[var(--color-text)]"
          >
            ← Portfolio
          </Link>
        }
      />
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <NumberField
            label="Cash to deploy ($)"
            value={cashDollars}
            onChange={(e) => setCashDollars(e.target.value)}
            min={0}
            step={100}
            className="max-w-xs"
          />
          <CheckboxField
            label="Allow sells (tax-deferred only)"
            checked={allowSells}
            onChange={(e) => setAllowSells(e.target.checked)}
          />
          <Button
            variant="primary"
            size="md"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Computing…" : "Compute"}
          </Button>
        </div>
      </Card>

      {isLoading && <p className="text-sm text-[var(--color-muted)]">Loading…</p>}
      {error && (
        <Card>
          <p className="text-sm text-[var(--color-danger)]">
            {error instanceof Error ? error.message : "Failed"}
          </p>
        </Card>
      )}
      {data && data.trades.length === 0 && data.notes.some((n) => n.includes("No target_allocation")) && (
        <EmptyState
          title="No target allocation set"
          description="Set fire_assumptions.target_allocation (e.g., {VTI: 60, VXUS: 30, BND: 10}) to enable rebalancing."
        />
      )}

      {data && Object.keys(data.target_allocation).length > 0 && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              label="Drift before"
              value={`${data.drift_before_pp.toFixed(1)} pp`}
              sub="Sum of |sleeve_pct − target_pct|"
            />
            <StatCard
              label="Drift after"
              value={`${data.drift_after_pp.toFixed(1)} pp`}
              sub={`Warn threshold: ${data.drift_warn_pp} pp`}
            />
            <StatCard
              label="Cash remaining"
              value={formatCentsCurrency(data.cash_inflow_remaining_cents)}
              sub={`of ${formatCentsCurrency(cashCents)} deployed`}
            />
          </div>
          <Card>
            <p className="mb-3 text-xs text-[var(--color-muted)]">{data.methodology}</p>
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="py-2">Sleeve</th>
                  <th className="py-2">Action</th>
                  <th className="py-2 text-right">Current</th>
                  <th className="py-2 text-right">Target</th>
                  <th className="py-2 text-right">Trade</th>
                  <th className="py-2">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border-subtle)]">
                {data.trades.map((t) => (
                  <tr key={t.sleeve}>
                    <td className="py-2 font-mono font-medium text-[var(--color-text)]">
                      {t.sleeve}
                    </td>
                    <td className="py-2">
                      <Pill
                        tone={
                          t.action === "buy"
                            ? "success"
                            : t.action === "sell"
                              ? "warning"
                              : "neutral"
                        }
                      >
                        {t.action}
                      </Pill>
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                      {formatCentsCurrency(t.current_value_cents)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-muted)]">
                      {formatCentsCurrency(t.target_value_cents)}
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                      {t.amount_cents > 0
                        ? formatCentsCurrency(t.amount_cents)
                        : "—"}
                    </td>
                    <td className="py-2 text-xs text-[var(--color-muted)]">
                      {t.skip_reason ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
          {data.notes.length > 0 && (
            <Card>
              <ul className="list-disc space-y-1 pl-5 text-xs text-[var(--color-muted)]">
                {data.notes.map((n, i) => (
                  <li key={i}>{n}</li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.portfolioRebalance} />
      </div>
    </div>
  );
}
